import { useState, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { AlertTriangle, Check, Search } from 'lucide-react';
import { Input } from '@/app/components/ui/input';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent } from '@/app/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { cn } from '@/app/components/ui/utils';
import type {
  TechProfileListItem,
  TechCertification,
  AvailabilityEntry,
} from '@/lib/types';
import { classifyCertsForJob, type CertIssue } from './cert-issues';

const CERT_TYPE_LABELS: Record<string, string> = {
  sprat_1: 'SPRAT Level 1',
  sprat_2: 'SPRAT Level 2',
  sprat_3: 'SPRAT Level 3',
  irata_1: 'IRATA Level 1',
  irata_2: 'IRATA Level 2',
  irata_3: 'IRATA Level 3',
  first_aid: 'First Aid',
  cpr: 'CPR',
  confined_space: 'Confined Space',
  fall_protection: 'Fall Protection',
  rigging: 'Rigging',
  crane_signal: 'Crane Signal',
  hazmat: 'HAZMAT',
  osha_10: 'OSHA 10',
  osha_30: 'OSHA 30',
  twic: 'TWIC',
  custom: 'Custom',
};

interface AssignmentTechPickerProps {
  techs: TechProfileListItem[];
  availability: AvailabilityEntry[];
  certifications: TechCertification[];
  startDate: string;
  endDate: string;
  onSelect: (techId: number) => void;
  selectedTechId?: number | null;
}

export function AssignmentTechPicker({
  techs,
  availability,
  certifications,
  startDate,
  endDate,
  onSelect,
  selectedTechId,
}: AssignmentTechPickerProps) {
  const [search, setSearch] = useState('');
  const [certFilter, setCertFilter] = useState<string>('all');

  // Build availability map: techId -> set of dates with "available" status
  const availabilityMap = useMemo(() => {
    const map = new Map<number, Map<string, string>>();
    availability.forEach((entry) => {
      if (!map.has(entry.tech)) map.set(entry.tech, new Map());
      map.get(entry.tech)!.set(entry.date, entry.status);
    });
    return map;
  }, [availability]);

  // Build cert map: techId -> certs
  const certMap = useMemo(() => {
    const map = new Map<number, TechCertification[]>();
    certifications.forEach((cert) => {
      if (!map.has(cert.tech)) map.set(cert.tech, []);
      map.get(cert.tech)!.push(cert);
    });
    return map;
  }, [certifications]);

  // Filter and sort techs
  const filteredTechs = useMemo(() => {
    let result = techs;

    // Search filter
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (t) =>
          t.full_name.toLowerCase().includes(q) ||
          t.job_title.toLowerCase().includes(q) ||
          t.email.toLowerCase().includes(q)
      );
    }

    // Cert filter
    if (certFilter && certFilter !== 'all') {
      const techIdsWithCert = new Set(
        certifications.filter((c) => c.cert_type === certFilter).map((c) => c.tech)
      );
      result = result.filter((t) => techIdsWithCert.has(t.id));
    }

    return result;
  }, [techs, search, certFilter, certifications]);

  const getTechAvailabilityStatus = (techId: number): 'available' | 'partial' | 'unavailable' | 'unknown' => {
    const techAvail = availabilityMap.get(techId);
    if (!techAvail) return 'unknown';

    // Check if tech has "available" for the date range
    let availableCount = 0;
    let recordedCount = 0;
    let totalDays = 0;
    if (startDate && endDate) {
      let current = parseISO(startDate);
      const end = parseISO(endDate);
      while (current <= end) {
        totalDays++;
        const dateStr = format(current, 'yyyy-MM-dd');
        if (techAvail.has(dateStr)) {
          recordedCount++;
        }
        if (techAvail.get(dateStr) === 'available') {
          availableCount++;
        }
        current = new Date(current.getTime() + 86400000);
      }
    }
    if (totalDays === 0 || recordedCount === 0) return 'unknown';
    if (availableCount === totalDays) return 'available';
    if (availableCount > 0) return 'partial';
    return 'unavailable';
  };

  const getCertIssues = (techId: number): CertIssue[] =>
    classifyCertsForJob(certMap.get(techId) ?? [], startDate, endDate);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search technicians..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={certFilter} onValueChange={setCertFilter}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Filter by cert" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Certifications</SelectItem>
            {Object.entries(CERT_TYPE_LABELS).map(([key, label]) => (
              <SelectItem key={key} value={key}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tech List */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {filteredTechs.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">
            No technicians match the current filters.
          </p>
        ) : (
          filteredTechs.map((tech) => {
            const availStatus = getTechAvailabilityStatus(tech.id);
            const certIssues = getCertIssues(tech.id);
            const isSelected = selectedTechId === tech.id;
            const techCerts = certMap.get(tech.id) ?? [];
            const revokedCerts = techCerts.filter((cert) => cert.status === 'revoked');

            return (
              <Card
                key={tech.id}
                className={cn(
                  'cursor-pointer transition-all hover:shadow-md',
                  isSelected && 'ring-2 ring-blue-500 bg-blue-50',
                  availStatus === 'unavailable' && 'opacity-60'
                )}
                onClick={() => onSelect(tech.id)}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isSelected && (
                        <div className="h-6 w-6 rounded-full bg-blue-500 flex items-center justify-center">
                          <Check className="h-4 w-4 text-white" />
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-sm">{tech.full_name}</div>
                        <div className="text-xs text-gray-500">{tech.job_title || 'No title'}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        className={cn(
                          'text-xs',
                          availStatus === 'available' && 'bg-green-100 text-green-800',
                          availStatus === 'partial' && 'bg-yellow-100 text-yellow-800',
                          availStatus === 'unavailable' && 'bg-red-100 text-red-800',
                          availStatus === 'unknown' && 'bg-gray-100 text-gray-700'
                        )}
                      >
                        {availStatus === 'available'
                          ? 'Available'
                          : availStatus === 'partial'
                          ? 'Partial'
                          : availStatus === 'unknown'
                          ? 'Unknown'
                          : 'Unavailable'}
                      </Badge>
                    </div>
                  </div>

                  {/* Cert badges */}
                  {techCerts.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {techCerts.slice(0, 4).map((cert) => (
                        <Badge
                          key={cert.id}
                          variant="outline"
                          className={cn(
                            'text-[10px]',
                            cert.status === 'valid' && 'border-green-300',
                            cert.status === 'expiring_soon' && 'border-yellow-300 bg-yellow-50',
                            cert.status === 'expired' && 'border-red-300 bg-red-50',
                            cert.status === 'revoked' && 'border-gray-400 bg-gray-100 text-gray-700'
                          )}
                        >
                          {cert.cert_name}
                        </Badge>
                      ))}
                      {techCerts.length > 4 && (
                        <Badge variant="outline" className="text-[10px]">
                          +{techCerts.length - 4} more
                        </Badge>
                      )}
                    </div>
                  )}

                  {revokedCerts.length > 0 && (
                    <div className="mt-2 flex items-start gap-1.5 text-xs font-medium text-gray-700">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                      <span>
                        {revokedCerts.length === 1
                          ? `${revokedCerts[0].cert_name} is revoked`
                          : `${revokedCerts.length} certifications are revoked`}
                      </span>
                    </div>
                  )}

                  {certIssues.map((issue) => (
                    <div
                      key={issue.cert.id}
                      className={cn(
                        'mt-2 flex items-start gap-1.5 text-xs',
                        issue.blocking ? 'font-medium text-red-600' : 'text-amber-600',
                      )}
                    >
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                      <span>{issue.message}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
